
import React, { Suspense, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  Container,
  Grid,
  Dialog,
  IconButton,
} from "@mui/material";
import styled from "styled-components";
import { getAuth, onAuthStateChanged } from "firebase/auth";

import QrCode2Icon from "@mui/icons-material/QrCode2";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ImageIcon from "@mui/icons-material/Image";
import StarIcon from "@mui/icons-material/Star";
import InstagramIcon from "@mui/icons-material/Instagram";
import TikTokIcon from "@mui/icons-material/MusicNote";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CameraAltOutlinedIcon from "@mui/icons-material/CameraAltOutlined";
import AllInclusiveIcon from "@mui/icons-material/AllInclusive";

import HeroImage from "../assets/hero.png";
import BoomerangVideo from "../assets/boomerang.mp4";

import { PricingGuestCountSection } from "../pages/Pricing";

const FAQ = React.lazy(() => import("../components/FAQ"));
const ChipList = React.lazy(() => import("../components/ChipList"));
const ReviewsSection = React.lazy(() =>
  import("../components/ReviewsSection.js")
);
const StorybookSection = React.lazy(() =>
  import("../components/StorybookSection")
);
const UnlimitedUploadsSection = React.lazy(() =>
  import("../components/UnlimitedUploadsSection")
);

// =====================================================
// HOLIDAY SPECIAL CONFIGURATION
// =====================================================

const HOLIDAY_OFFER_ENABLED = true;
const HOLIDAY_POPUP_DELAY = 5000;
const HOLIDAY_SESSION_KEY =
  "elevate_holiday_special_2026_dismissed";

const HOLIDAY_GREEN = "#8DE4A9";
const HOLIDAY_GUEST_LIMIT = 100;

// =====================================================
// HOLIDAY SPECIAL POPUP
// =====================================================

const HolidayOfferPopup = ({ open, onClose }) => (
  <Dialog
    open={open}
    onClose={onClose}
    aria-labelledby="holiday-offer-title"
    aria-describedby="holiday-offer-description"
    maxWidth="xs"
    fullWidth
    PaperProps={{
      sx: {
        width: "calc(100% - 32px)",
        maxWidth: 410,
        m: 2,
        borderRadius: "24px",
        background: "#111113",
        color: "#fff",
        overflow: "hidden",
        border: "1px solid rgba(255,255,255,0.14)",
        boxShadow: "0 30px 100px rgba(0,0,0,0.55)",
      },
    }}
    BackdropProps={{
      sx: {
        backgroundColor: "rgba(0,0,0,0.67)",
      },
    }}
  >
    <Box
      sx={{
        position: "relative",
        px: { xs: 2.5, sm: 4 },
        pt: 4.5,
        pb: 3.5,
        textAlign: "center",
      }}
    >
      <IconButton
        onClick={onClose}
        aria-label="Close holiday special"
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          color: "rgba(255,255,255,0.65)",
          width: 36,
          height: 36,
          "&:hover": {
            background: "rgba(255,255,255,0.08)",
            color: "#fff",
          },
        }}
      >
        <CloseRoundedIcon sx={{ fontSize: 21 }} />
      </IconButton>

      <Typography
        sx={{
          fontSize: 10,
          fontWeight: 800,
          letterSpacing: "0.16em",
          color: "#b9bac0",
          mb: 1.5,
        }}
      >
        ELEVATE HOLIDAY SPECIAL
      </Typography>

      <Typography
        id="holiday-offer-title"
        component="h2"
        sx={{
          fontSize: { xs: "1.85rem", sm: "2.15rem" },
          fontWeight: 900,
          lineHeight: 1.12,
          letterSpacing: "-0.045em",
          color: "#fff",
          mb: 1.5,
        }}
      >
        Your holiday
        <Box component="span" sx={{ display: "block" }}>
          special is here. ✨
        </Box>
      </Typography>

      <Typography
        id="holiday-offer-description"
        sx={{
          fontSize: 16,
          lineHeight: 1.55,
          fontWeight: 850,
          color: HOLIDAY_GREEN,
          maxWidth: 310,
          mx: "auto",
          mb: 2.5,
        }}
      >
        Unlimited Photos &amp; Boomerangs.
        <Box
          component="span"
          sx={{
            display: "block",
            mt: 0.6,
            color: "#c6c6cc",
            fontSize: 13,
            fontWeight: 500,
          }}
        >
          Every moment in one shared gallery.
        </Box>
      </Typography>

      <Box
        sx={{
          background:
            "linear-gradient(145deg, #242429 0%, #1a1a1e 100%)",
          border: "1px solid rgba(255,255,255,0.10)",
          borderRadius: "17px",
          px: 2.5,
          py: 2.5,
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 0.75,
            mb: 1.25,
          }}
        >
          <Box
            sx={{
              width: 6,
              height: 6,
              borderRadius: "50%",
              backgroundColor: "#63d990",
            }}
          />

          <Typography
            sx={{
              fontSize: 14,
              fontWeight: 800,
              color: HOLIDAY_GREEN,
            }}
          >
            Limited-time offer · Ending soon
          </Typography>
        </Box>

        <Typography
          sx={{
            fontSize: "2.7rem",
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: "-0.06em",
            color: "#fff",
          }}
        >
          $34.99
        </Typography>

        <Typography
          sx={{
            fontSize: 12,
            color: "#aaaab3",
            mt: 0.75,
            mb: 2,
          }}
        >
          One-time payment · Up to {HOLIDAY_GUEST_LIMIT} guests
        </Typography>

        <Box
          sx={{
            height: "1px",
            backgroundColor: "rgba(255,255,255,0.10)",
            mb: 2,
          }}
        />

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1.25,
            alignItems: "flex-start",
            maxWidth: 245,
            mx: "auto",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <CameraAltOutlinedIcon
              sx={{ fontSize: 19, color: "#a9cfff" }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 750,
                color: HOLIDAY_GREEN,
              }}
            >
              Unlimited photo uploads
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <AllInclusiveIcon
              sx={{ fontSize: 19, color: "#a9cfff" }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 750,
                color: HOLIDAY_GREEN,
              }}
            >
              Unlimited Boomerangs
            </Typography>
          </Box>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <QrCode2Icon
              sx={{ fontSize: 19, color: "#a9cfff" }}
            />
            <Typography
              sx={{
                fontSize: 13,
                fontWeight: 750,
                color: "#fff",
              }}
            >
              Your custom QR code + link
            </Typography>
          </Box>
        </Box>
      </Box>

      <Button
        component={Link}
        to="/signup"
        onClick={onClose}
        fullWidth
        endIcon={<ArrowForwardRoundedIcon />}
        sx={{
          minHeight: 52,
          borderRadius: "13px",
          backgroundColor: "#fff",
          color: "#111",
          textTransform: "none",
          fontWeight: 850,
          fontSize: 15,
          "&:hover": {
            backgroundColor: "#e8e8eb",
          },
        }}
      >
        Claim Holiday Special
      </Button>

      <Typography
        sx={{
          mt: 2,
          fontSize: 11,
          color: "#888890",
        }}
      >
        One QR code. Every memory.
      </Typography>
    </Box>
  </Dialog>
);

// =====================================================
// PERMANENT HOLIDAY PROMOTIONAL BANNER
// =====================================================

const HolidayOfferBanner = () => (
  <Box
    component="section"
    aria-label="Holiday Special offer"
    sx={{
      position: "relative",
      zIndex: 2,
      backgroundColor: "#0b0b0c",
      px: { xs: 2, sm: 3 },
      pt: { xs: 1, md: 2 },
      pb: { xs: 3, md: 4 },
    }}
  >
    <Container maxWidth="lg" disableGutters>
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: "20px", md: "24px" },
          border: "1px solid rgba(255,255,255,0.13)",
          background:
            "linear-gradient(115deg, #1d2028 0%, #15161a 48%, #111214 100%)",
          boxShadow: "0 14px 36px rgba(0,0,0,0.20)",
          px: { xs: 2.5, sm: 3.5, md: 4 },
          py: { xs: 3, md: 3.25 },
        }}
      >
        <Box
          sx={{
            position: "absolute",
            right: "-70px",
            top: "-110px",
            width: 280,
            height: 280,
            borderRadius: "50%",
            background:
              "radial-gradient(circle, rgba(121,135,164,0.16), transparent 70%)",
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            display: "flex",
            alignItems: {
              xs: "flex-start",
              md: "center",
            },
            justifyContent: "space-between",
            flexDirection: {
              xs: "column",
              md: "row",
            },
            gap: { xs: 2.5, md: 3 },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                mb: 1.25,
              }}
            >
              <Box
                sx={{
                  width: 7,
                  height: 7,
                  borderRadius: "50%",
                  backgroundColor: HOLIDAY_GREEN,
                }}
              />

              <Typography
                sx={{
                  fontSize: 10,
                  fontWeight: 850,
                  letterSpacing: "0.13em",
                  color: HOLIDAY_GREEN,
                }}
              >
                LIMITED-TIME HOLIDAY SPECIAL
              </Typography>
            </Box>

            <Typography
              component="h2"
              sx={{
                color: "#fff",
                fontSize: {
                  xs: "1.55rem",
                  sm: "1.8rem",
                  md: "1.95rem",
                },
                fontWeight: 900,
                letterSpacing: "-0.045em",
                lineHeight: 1.12,
                mb: 1.1,
              }}
            >
              Plan now. Party later.
            </Typography>

            <Typography
              sx={{
                color: HOLIDAY_GREEN,
                fontSize: { xs: 15, sm: 16 },
                fontWeight: 850,
                lineHeight: 1.5,
              }}
            >
              Unlimited Photos &amp; Boomerangs
              <Box
                component="span"
                sx={{
                  display: "block",
                  mt: 0.4,
                  color: "#c5c6cc",
                  fontSize: { xs: 12, sm: 13 },
                  fontWeight: 500,
                }}
              >
                Up to {HOLIDAY_GUEST_LIMIT} guests · One shared gallery
              </Box>
            </Typography>
          </Box>

          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "stretch",
                sm: "center",
              },
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              width: { xs: "100%", md: "auto" },
              flexShrink: 0,
            }}
          >
            <Box
              sx={{
                textAlign: { xs: "left", sm: "right" },
              }}
            >
              <Typography
                sx={{
                  color: "#fff",
                  fontSize: "2rem",
                  fontWeight: 900,
                  letterSpacing: "-0.055em",
                  lineHeight: 1,
                }}
              >
                $34.99
              </Typography>

              <Typography
                sx={{
                  mt: 0.5,
                  fontSize: 11,
                  color: "#aeb0b9",
                }}
              >
                One-time payment
              </Typography>
            </Box>

            <Button
              component={Link}
              to="/signup"
              endIcon={<ArrowForwardRoundedIcon />}
              sx={{
                minHeight: 48,
                px: 2.5,
                borderRadius: "12px",
                backgroundColor: "#fff",
                color: "#111",
                fontSize: 13,
                fontWeight: 850,
                textTransform: "none",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "#e8e8eb",
                },
              }}
            >
              Claim Holiday Special
            </Button>
          </Box>
        </Box>
      </Box>
    </Container>
  </Box>
);

// =====================================================
// HOLIDAY PROMOTION CONTROLLER
// =====================================================

const HolidayPromotion = () => {
  const [isLoggedOut, setIsLoggedOut] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  useEffect(() => {
    if (!HOLIDAY_OFFER_ENABLED) return undefined;

    let active = true;
    let timer = null;

    const wasDismissed = () => {
      try {
        return (
          sessionStorage.getItem(HOLIDAY_SESSION_KEY) === "true"
        );
      } catch {
        return false;
      }
    };

    const unsubscribe = onAuthStateChanged(
      getAuth(),
      (user) => {
        if (!active) return;

        if (timer !== null) {
          clearTimeout(timer);
          timer = null;
        }

        if (user) {
          setIsLoggedOut(false);
          setShowPopup(false);
          return;
        }

        setIsLoggedOut(true);

        if (!wasDismissed()) {
          timer = setTimeout(() => {
            if (active && !wasDismissed()) {
              setShowPopup(true);
            }
          }, HOLIDAY_POPUP_DELAY);
        }
      },
      () => {
        if (active) {
          setIsLoggedOut(false);
          setShowPopup(false);
        }
      }
    );

    return () => {
      active = false;
      if (timer !== null) clearTimeout(timer);
      unsubscribe();
    };
  }, []);

  const handleClose = () => {
    setShowPopup(false);

    try {
      sessionStorage.setItem(HOLIDAY_SESSION_KEY, "true");
    } catch {
      // Closing works even when session storage is unavailable.
    }
  };

  if (!HOLIDAY_OFFER_ENABLED || !isLoggedOut) {
    return null;
  }

  return (
    <>
      <HolidayOfferBanner />
      <HolidayOfferPopup
        open={showPopup}
        onClose={handleClose}
      />
    </>
  );
};

// =====================================================
// DARK HOMEPAGE HERO
// =====================================================

const HeroSection = styled(Box)`
  position: relative;
  width: 100%;
  overflow: hidden;
  isolation: isolate;
  color: #fff;

  background:
    radial-gradient(
      ellipse at 50% 82%,
      rgba(47, 51, 61, 0.20),
      transparent 55%
    ),
    linear-gradient(
      180deg,
      #090909 0%,
      #0b0b0c 72%,
      #101113 100%
    );

  @media (min-width: 900px) {
    min-height: 760px;
    display: flex;
    align-items: center;
  }
`;

const HeroInner = styled(Box)`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  align-items: center;

  @media (min-width: 900px) {
    grid-template-columns:
      minmax(0, 0.92fr)
      minmax(0, 1.08fr);

    gap: 20px;
    min-height: 760px;
  }
`;

const LeftCol = styled(Box)`
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 38px 8px 0;

  @media (min-width: 900px) {
    align-items: flex-start;
    text-align: left;
    padding: 55px 0 55px 12px;
  }
`;

const TrustBadge = styled(Box)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  margin-bottom: 24px;
  padding: 9px 14px;
  background: rgba(255, 255, 255, 0.055);
  border: 1px solid rgba(255, 255, 255, 0.13);
  border-radius: 999px;
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
`;

const HeroTitle = styled(Typography)`
  && {
    color: #fff;
    font-family: inherit;
    font-size: clamp(37px, 10vw, 54px);
    font-weight: 900;
    line-height: 1.025;
    letter-spacing: -0.06em;
    max-width: 670px;
    margin: 0;
    text-wrap: balance;

    @media (min-width: 600px) {
      font-size: clamp(48px, 7vw, 66px);
    }

    @media (min-width: 900px) {
      font-size: clamp(54px, 5vw, 76px);
    }
  }
`;

const HeroDescription = styled(Typography)`
  && {
    color: #b9bac0;
    font-size: clamp(15px, 3.8vw, 18px);
    font-weight: 400;
    line-height: 1.55;
    letter-spacing: -0.012em;
    max-width: 510px;
    margin-top: 20px;

    @media (min-width: 900px) {
      font-size: 18px;
      margin-top: 26px;
    }
  }
`;

const PrimaryCTA = styled(Button)`
  && {
    background: #fff;
    color: #0c0c0e;
    border: 1px solid rgba(255, 255, 255, 0.85);
    border-radius: 15px;
    padding: 15px 24px;
    min-height: 56px;
    font-weight: 800;
    font-size: 16px;
    text-transform: none;
    letter-spacing: -0.015em;

    box-shadow:
      0 16px 42px rgba(0, 0, 0, 0.3),
      0 4px 12px rgba(0, 0, 0, 0.15);

    transition:
      background 0.2s ease,
      transform 0.2s ease,
      box-shadow 0.2s ease;

    &:hover {
      background: #e9e9ec;
      color: #000;
      transform: translateY(-2px);
      box-shadow: 0 22px 48px rgba(0, 0, 0, 0.38);
    }
  }
`;

const HeroActions = styled(Box)`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 27px;

  @media (min-width: 900px) {
    justify-content: flex-start;
    margin-top: 32px;
  }
`;

const HeroBenefits = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 10px 16px;
  margin-top: 24px;

  @media (min-width: 900px) {
    justify-content: flex-start;
    margin-top: 28px;
  }
`;

const HeroBenefit = styled(Box)`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #c8c8cd;
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;

  @media (min-width: 600px) {
    font-size: 13px;
  }
`;

// =====================================================
// PRODUCT SHOWCASE
// =====================================================

const RightCol = styled(Box)`
  position: relative;
  width: 100%;
  min-width: 0;
  margin-top: 38px;

  @media (min-width: 600px) {
    margin-top: 44px;
  }

  @media (min-width: 900px) {
    margin-top: 0;
  }
`;

const ProductStage = styled(Box)`
  position: relative;
  width: 100%;
  height: clamp(620px, 166vw, 850px);
  overflow: hidden;
  isolation: isolate;

  @media (min-width: 600px) {
    height: 890px;
  }

  @media (min-width: 900px) {
    height: 755px;
  }
`;

const StageLight = styled(Box)`
  position: absolute;
  z-index: 0;
  left: 44%;
  top: 42%;
  width: 96%;
  height: 75%;
  transform: translate(-50%, -35%);
  border-radius: 50%;
  background: radial-gradient(
    ellipse,
    rgba(101, 110, 129, 0.13) 0%,
    rgba(55, 61, 73, 0.055) 45%,
    transparent 74%
  );
  filter: blur(35px);
  pointer-events: none;
`;

// =====================================================
// MAIN IPHONE
// =====================================================

const PhonePosition = styled(Box)`
  position: absolute;
  z-index: 3;
  top: 10px;
  left: -63%;
  width: 205%;
  transform: rotate(-6deg);
  transform-origin: center top;

  @media (min-width: 600px) {
    top: 12px;
    left: -48%;
    width: 186%;
  }

  @media (min-width: 900px) {
    top: 42px;
    left: -50%;
    width: 193%;
  }
`;

const PhoneImage = styled("img")`
  display: block;
  width: 100%;
  height: auto;
  max-width: none;
  user-select: none;
  pointer-events: none;
  -webkit-user-drag: none;

  filter:
    drop-shadow(0 13px 11px rgba(0, 0, 0, 0.22))
    drop-shadow(0 33px 31px rgba(0, 0, 0, 0.46))
    drop-shadow(0 66px 60px rgba(0, 0, 0, 0.3));
`;

// =====================================================
// BOOMERANG PREVIEW — NATIVE AUTOPLAY
// =====================================================

const BOOMERANG_ZOOM = 1.20;

const BoomerangPosition = styled(Box)`
  position: absolute;
  z-index: 4;
  right: 3%;
  top: 53%;
  bottom: auto;
  width: 40%;
  transform: rotate(2deg);
  transform-origin: center top;

  @media (max-width: 380px) {
    right: 3%;
    top: 53%;
    width: 39%;
  }

  @media (min-width: 600px) {
    right: 7%;
    top: 53%;
    width: 36%;
  }

  @media (min-width: 900px) {
    right: 4%;
    top: 53%;
    width: 38%;
  }
`;

const BoomerangShadow = styled(Box)`
  position: absolute;
  z-index: -1;
  inset: -15% -20% 0 -20%;
  pointer-events: none;

  &::before {
    content: "";
    position: absolute;
    inset: 10% 5% 4%;
    border-radius: 45%;
    background: rgba(0, 0, 0, 0.32);
    filter: blur(16px);
  }

  &::after {
    content: "";
    position: absolute;
    left: -12%;
    right: -12%;
    bottom: -15%;
    height: 65%;
    border-radius: 50%;
    background: radial-gradient(
      ellipse,
      rgba(0, 0, 0, 0.32) 0%,
      rgba(0, 0, 0, 0.12) 48%,
      transparent 78%
    );
    filter: blur(25px);
  }
`;

const BoomerangDevice = styled(Box)`
  position: relative;
  width: 100%;
  aspect-ratio: 9 / 16;
  padding: 2px;
  box-sizing: border-box;
  border-radius: 21px;

  background: linear-gradient(
    145deg,
    #545861 0%,
    #292c33 16%,
    #101114 40%,
    #202228 75%,
    #464951 100%
  );

  border: 1px solid rgba(137, 143, 155, 0.34);

  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    inset 0 -1px 0 rgba(0, 0, 0, 0.28),
    0 5px 10px rgba(0, 0, 0, 0.28),
    0 15px 25px rgba(0, 0, 0, 0.36),
    0 32px 45px rgba(0, 0, 0, 0.22);

  @media (min-width: 600px) {
    border-radius: 26px;
  }
`;

const BoomerangScreen = styled(Box)`
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
  border-radius: 18px;
  background: #101114;

  @media (min-width: 600px) {
    border-radius: 23px;
  }
`;

const HeroVideo = styled("video")`
  position: absolute;
  inset: 0;
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: 20% center;
  transform: scale(${BOOMERANG_ZOOM});
  transform-origin: center center;
  background: #111;
  pointer-events: none;

  filter:
    contrast(1.05)
    saturate(1.08)
    brightness(1.02);

  backface-visibility: hidden;
  -webkit-backface-visibility: hidden;
`;

const CinematicOverlay = styled(Box)`
  position: absolute;
  z-index: 2;
  inset: 0;
  pointer-events: none;

  background:
    linear-gradient(
      135deg,
      rgba(255, 255, 255, 0.055) 0%,
      transparent 25%,
      transparent 85%
    ),
    linear-gradient(
      180deg,
      rgba(0, 0, 0, 0.05) 0%,
      transparent 22%,
      transparent 78%,
      rgba(0, 0, 0, 0.07) 100%
    );
`;

const BoomerangCaption = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 100%;
  margin-top: 11px;
  color: rgba(255, 255, 255, 0.88);
  font-family: inherit;
  font-size: 11px;
  font-weight: 650;
  letter-spacing: 0.01em;
  white-space: nowrap;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.28);

  @media (min-width: 600px) {
    font-size: 12px;
    margin-top: 14px;
  }
`;

const BoomerangPreview = () => {
  const [videoError, setVideoError] = useState(false);

  return (
    <BoomerangPosition>
      <BoomerangShadow />

      <BoomerangDevice>
        <BoomerangScreen>
          {videoError ? (
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#151515",
                color: "#fff",
                p: 1,
                textAlign: "center",
              }}
            >
              <Typography sx={{ fontSize: 12 }}>
                Boomerang Clips
              </Typography>
            </Box>
          ) : (
            <HeroVideo
              src={BoomerangVideo}
              autoPlay
              muted
              loop
              playsInline
              preload="auto"
              controls={false}
              disablePictureInPicture
              disableRemotePlayback
              onError={() => setVideoError(true)}
              aria-label="Elevate Boomerang video preview"
            />
          )}

          <CinematicOverlay />
        </BoomerangScreen>
      </BoomerangDevice>

      <BoomerangCaption>
        <AllInclusiveIcon
          sx={{
            fontSize: 18,
            color: "#e8e8eb",
          }}
        />
        Boomerang Clips
      </BoomerangCaption>
    </BoomerangPosition>
  );
};

// =====================================================
// OTHER HOMEPAGE COMPONENTS
// =====================================================

const DarkSection = styled(Box)`
  background: #0b0b0c;
  color: #fff;
`;

const SectionHeading = styled(Typography)`
  && {
    font-family: inherit;
    font-weight: 850;
    font-size: clamp(29px, 5vw, 40px);
    line-height: 1.15;
    letter-spacing: -0.045em;
    color: #fff;
    text-align: center;
  }
`;

const StepCard = styled(Box)`
  width: 100%;
  height: 100%;
  max-width: 520px;
  margin: 0 auto;
  padding: 30px 24px;
  box-sizing: border-box;
  text-align: center;

  background: linear-gradient(
    155deg,
    #1b1c20 0%,
    #121315 100%
  );

  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 24px;
  box-shadow: 0 18px 36px rgba(0, 0, 0, 0.18);

  transition:
    transform 0.25s ease,
    border-color 0.25s ease;

  &:hover {
    transform: translateY(-4px);
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

const StepIcon = styled(Box)`
  width: 76px;
  height: 76px;
  margin: 0 auto 22px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  background: #292a2e;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 22px;
`;

const StepTitle = styled(Typography)`
  && {
    color: #fff;
    font-size: 19px;
    font-weight: 800;
    line-height: 1.3;
    letter-spacing: -0.025em;
    margin-bottom: 10px;
  }
`;

const StepDescription = styled(Typography)`
  && {
    color: #aeb0b7;
    font-size: 14px;
    line-height: 1.65;
    max-width: 340px;
    margin: 0 auto;
  }
`;

const SectionFallback = () => (
  <Box sx={{ minHeight: 24 }} />
);

// =====================================================
// HOME
// =====================================================

const Home = () => {
  return (
    <Box
      sx={{
        width: "100%",
        m: 0,
        p: 0,
        overflowX: "hidden",
        backgroundColor: "#0b0b0c",
        color: "#fff",
      }}
    >
      {/* HERO */}

      <HeroSection>
        <Container
          maxWidth="lg"
          sx={{ position: "relative" }}
        >
          <HeroInner>
            <LeftCol>
              <TrustBadge>
                <StarIcon
                  sx={{
                    fontSize: 15,
                    color: "#fff",
                  }}
                />

                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 800,
                    color: "#fff",
                  }}
                >
                  4.9
                </Typography>

                <Box
                  sx={{
                    height: 12,
                    width: "1px",
                    bgcolor: "rgba(255,255,255,0.23)",
                    mx: 0.3,
                  }}
                />

                <Typography
                  component="span"
                  sx={{
                    fontSize: 12,
                    fontWeight: 650,
                    color: "#d4d4d7",
                  }}
                >
                  Trusted by 1,000+ events
                </Typography>
              </TrustBadge>

              <HeroTitle component="h1">
                Get Every

                <Box
                  component="span"
                  sx={{ display: "block" }}
                >
                  Guest Photo.
                </Box>

                <Box
                  component="span"
                  sx={{
                    display: "block",
                    color: "#92949b",
                  }}
                >
                  Instantly.
                </Box>
              </HeroTitle>

              <HeroDescription>
                Every guest takes photos on their own phone.
                Elevate brings them all together in one place.
              </HeroDescription>

              <HeroActions>
                <PrimaryCTA
                  component={Link}
                  to="/signup"
                  aria-label="Create an event"
                  endIcon={
                    <ArrowForwardRoundedIcon
                      sx={{ fontSize: 20 }}
                    />
                  }
                  sx={{
                    width: {
                      xs: "min(100%, 340px)",
                      md: "auto",
                    },
                    minWidth: { md: 230 },
                  }}
                >
                  Create an Event
                </PrimaryCTA>
              </HeroActions>

              <HeroBenefits>
                <HeroBenefit>
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 15,
                      color: "#d8d8dc",
                    }}
                  />
                  No app downloads
                </HeroBenefit>

                <HeroBenefit>
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 15,
                      color: "#d8d8dc",
                    }}
                  />
                  No guest logins
                </HeroBenefit>

                <HeroBenefit>
                  <CheckCircleRoundedIcon
                    sx={{
                      fontSize: 15,
                      color: "#d8d8dc",
                    }}
                  />
                  One QR code
                </HeroBenefit>
              </HeroBenefits>
            </LeftCol>

            <RightCol>
              <ProductStage>
                <StageLight />

                <PhonePosition>
                  <PhoneImage
                    src={HeroImage}
                    alt="Elevate event photo gallery on iPhone"
                    decoding="async"
                    fetchPriority="high"
                    draggable={false}
                  />
                </PhonePosition>

                <BoomerangPreview />
              </ProductStage>
            </RightCol>
          </HeroInner>
        </Container>
      </HeroSection>

      {/* HOLIDAY BANNER + POPUP */}

      <HolidayPromotion />

      {/* FEATURES / REVIEWS / PRICING */}

      <Suspense fallback={<SectionFallback />}>
        <Box
          sx={{
            mt: {
              xs: 0,
              sm: -3,
              md: -2,
            },
            position: "relative",
            zIndex: 0,
          }}
        >
          <ChipList />
        </Box>

        <ReviewsSection />

        <PricingGuestCountSection
          title={
            <>
              How many guests are
              <br />
              you expecting?
            </>
          }
          subtitle="Select the range that matches your event."
          showTip={false}
        />

        <UnlimitedUploadsSection />
      </Suspense>

      <Suspense fallback={<SectionFallback />}>
        <StorybookSection />
      </Suspense>

      {/* HOW IT WORKS */}

      <DarkSection
        sx={{
          pt: 9,
          pb: 5,
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            component="h2"
            sx={{ mb: 5 }}
          >
            How It Works
          </SectionHeading>

          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4}>
              <StepCard>
                <StepIcon>
                  <CalendarTodayIcon
                    sx={{ fontSize: 35 }}
                  />
                </StepIcon>

                <StepTitle>
                  Step 1: Create Your Event
                </StepTitle>

                <StepDescription>
                  Set up your event and unlock a personal
                  gallery for your big day.
                </StepDescription>
              </StepCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <StepCard>
                <StepIcon>
                  <QrCode2Icon
                    sx={{ fontSize: 35 }}
                  />
                </StepIcon>

                <StepTitle>
                  Step 2: Share your QR Code
                </StepTitle>

                <StepDescription>
                  Send the link or share your QR code
                  before the big day.
                </StepDescription>
              </StepCard>
            </Grid>

            <Grid item xs={12} sm={6} md={4}>
              <StepCard>
                <StepIcon>
                  <ImageIcon
                    sx={{ fontSize: 35 }}
                  />
                </StepIcon>

                <StepTitle>
                  Step 3: Guests Join In
                </StepTitle>

                <StepDescription>
                  Guests scan, upload photos, and make
                  Boomerangs in seconds.
                </StepDescription>
              </StepCard>
            </Grid>
          </Grid>
        </Container>
      </DarkSection>

      {/* FINAL CTA */}

      <DarkSection
        sx={{
          pt: 8,
          pb: 8,
        }}
      >
        <Container
          maxWidth="lg"
          sx={{ textAlign: "center" }}
        >
          <SectionHeading
            component="h2"
            sx={{ mb: 3 }}
          >
            Ready to Transform Your Events?
          </SectionHeading>

          <PrimaryCTA
            component={Link}
            to="/signup"
          >
            Start Now
          </PrimaryCTA>
        </Container>
      </DarkSection>

      {/* FAQ */}

      <DarkSection
        sx={{
          pt: 5,
          pb: 8,
        }}
      >
        <Container maxWidth="lg">
          <SectionHeading
            component="h2"
            sx={{
              mb: 4,
              textAlign: "left !important",
            }}
          >
            Frequently Asked Questions
          </SectionHeading>

          <Box
            sx={{
              background: "#f9f9f9",
              color: "#111",
              borderRadius: "22px",
              p: {
                xs: 2,
                md: 3,
              },
              overflow: "hidden",
            }}
          >
            <Suspense fallback={<SectionFallback />}>
              <FAQ />
            </Suspense>
          </Box>
        </Container>
      </DarkSection>

      {/* FOOTER */}

      <Box
        sx={{
          py: 6,
          backgroundColor: "#080809",
          color: "#fff",
          borderTop: "1px solid rgba(255,255,255,0.09)",
        }}
      >
        <Container maxWidth="lg">
          <Grid
            container
            spacing={3}
            alignItems="flex-start"
          >
            <Grid item xs={12} md={6}>
              <Typography
                sx={{
                  fontWeight: 900,
                  fontSize: "1.2rem",
                  mb: 1,
                  letterSpacing: "-0.03em",
                }}
              >
                Elevate
              </Typography>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.6)",
                  maxWidth: 520,
                  lineHeight: 1.7,
                  fontSize: "14px",
                }}
              >
                Elevate is a QR code photo-sharing platform
                for events. Create an event, share your QR code,
                and guests upload photos instantly to one
                shared gallery.
              </Typography>
            </Grid>

            <Grid item xs={12} md={6}>
              <Box
                sx={{
                  display: "flex",
                  gap: 2,
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: {
                    xs: "flex-start",
                    md: "flex-end",
                  },
                  mt: {
                    xs: 1,
                    md: 0,
                  },
                }}
              >
                <Link
                  to="/about"
                  style={{
                    textDecoration: "none",
                    color: "#fff",
                    fontWeight: 700,
                  }}
                >
                  About
                </Link>

                <Link
                  to="/pricing"
                  style={{
                    textDecoration: "none",
                    color: "#fff",
                    fontWeight: 700,
                  }}
                >
                  Pricing
                </Link>

                <Link
                  to="/privacy-policy"
                  style={{
                    textDecoration: "none",
                    color: "#fff",
                    fontWeight: 700,
                  }}
                >
                  Privacy Policy
                </Link>

                <Link
                  to="/blog"
                  style={{
                    textDecoration: "none",
                    color: "#fff",
                    fontWeight: 700,
                  }}
                >
                  Blog
                </Link>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  gap: 1,
                  alignItems: "center",
                  justifyContent: {
                    xs: "flex-start",
                    md: "flex-end",
                  },
                  mt: 2,
                }}
              >
                <Box
                  component="a"
                  href="https://www.instagram.com/elevateevent_co"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Elevate on Instagram"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 42,
                    height: 42,
                    borderRadius: "13px",
                    backgroundColor: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  <InstagramIcon />
                </Box>

                <Box
                  component="a"
                  href="https://www.tiktok.com/@elevateevent.co"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label="Elevate on TikTok"
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: 42,
                    height: 42,
                    borderRadius: "13px",
                    backgroundColor: "rgba(255,255,255,0.07)",
                    border: "1px solid rgba(255,255,255,0.08)",
                    color: "#fff",
                    textDecoration: "none",
                  }}
                >
                  <TikTokIcon />
                </Box>
              </Box>

              <Typography
                sx={{
                  color: "rgba(255,255,255,0.45)",
                  mt: 2,
                  fontSize: "0.85rem",
                  textAlign: {
                    xs: "left",
                    md: "right",
                  },
                }}
              >
                © {new Date().getFullYear()} Elevate
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
    </Box>
  );
};

export default Home;
